// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// K · O «POR PLANEAR» VÊ-SE NO MAPA, NO TEMPO — §11 do desenho da agilidade
// ============================================================================
//
// 📗 «Hoje a fila vive numa lista e a planeadora casa datas de cabeça com a grelha.
//    Desenhada no fundo, alinhada nos dias dela, vê o buraco e a viagem que lá cabe
//    na mesma coluna.»
//
// ---------------------------------------------------------------------------
// ⚠️⚠️ PORQUE É QUE ISTO NÃO REUTILIZA `repartirPorLinhas`/`CelulaCompacta`
// ---------------------------------------------------------------------------
//
//   A secção «Em espera» (`linhas-do-subcontratado.ts`) reutiliza a MÁQUINA dos
//   subcontratados porque uma viagem em espera É uma alocação real — tem corrida,
//   itinerário, localidade por dia, tudo o que uma `CelulaCompacta` sabe guardar.
//   Só lhe falta a viatura.
//
//   Um pedido «por planear» é outra coisa: **não existe alocação nenhuma**. O que
//   há é um intervalo `[dia0, dia1]` conhecido, um cliente e uma referência — nada
//   de localidade por dia, nada de bandeiras, nada de corrida (a corrida só nasce
//   quando alguém planeia). Forçar isto pela forma de `CelulaCompacta` obrigava a
//   inventar 13 dos 17 campos do contrato (motorista, estado, meio, …) e a resolver
//   o cliente por SIGLA em vez de por índice — um contrato pensado para alocações
//   reais a fingir que descreve um pedido é «vestir a roupa de outra coisa».
//
//   O que SE reutiliza é o resto da máquina: a mesma altura de linha
//   (`ALTURA_LINHA_SUB`/`formaDaSeccaoSub`, que já é genérica — só olha para o
//   comprimento), a mesma secção sem tecto de linhas, e a mesma exclusão do
//   orçamento de nós do risco R1 (não é `viaturas × dias`). E a MESMA definição de
//   «por planear» do resto da aplicação: `separarListas` (`pedido.ts`) — regra 8,
//   uma só formulação. Escrever aqui uma segunda leitura do estado do pedido
//   divergiria da lista, e a que divergisse seria a que ninguém corre.
//
// ---------------------------------------------------------------------------
// ⚠️ «SEM DATA LEGÍVEL» ≠ «FORA DO ANO» — o precedente é `EscalaDosDias.tsx`
// ---------------------------------------------------------------------------
//
//   `EscalaDosDias.tsx` já separa estes dois: «um dia SEM DATA legível não tem
//   coluna nenhuma onde caiba, e um dia FORA DO INTERVALO escolhido dizem-se em
//   frases diferentes, porque são coisas diferentes». Aqui é a mesma distinção:
//
//     `semData`  · `dia0`/`dia1` vêm ambos a `-1` — a API (`diaDoAno` em
//                  `apps/api/src/pedidos.ts`) devolve -1 quando a data não é um
//                  ISO válido. Não se desenha nada, e diz-se quantos.
//     `foraDoAno`· a data É legível mas cai fora do ano deste plano (uma viagem
//                  que começa ou acaba no ano seguinte/anterior). Também não se
//                  desenha — este mapa é de UM ano — mas é um facto diferente e
//                  conta-se à parte.
//
//   ⚠️ **Ambiguidade herdada e não corrigida aqui:** `diaDoAno` também devolve -1
//   para uma data genuinamente válida do DIA ANTERIOR ao início do ano (31 de
//   Dezembro do ano anterior às 00:00 UTC cai exactamente um dia antes de
//   `Date.UTC(ano, 0, 1)`). Não há como distinguir os dois casos a partir do que
//   a API envia hoje — e corrigir isso é em `apps/api/src/pedidos.ts`, fora da
//   fronteira desta peça. Fica declarado: um pedido assim conta como `semData`
//   quando devia contar como `foraDoAno`. Dado o horizonte real (~120 pedidos por
//   planear, medida do gerador), é uma classificação errada rara e sem
//   consequência — o pedido não se desenha em nenhum dos dois casos.
//
//   ⚠️ Um pedido PARCIALMENTE fora do ano (começa antes de 1 de Janeiro, acaba
//   dentro do ano, ou vice-versa) é RECORTADO aos dias que existem neste ano — a
//   mesma decisão que a `janela` já toma para as barras da frota — e não entra em
//   nenhuma das duas contagens: mostra-se o que se sabe, não se esconde.
//
// ---------------------------------------------------------------------------
// ⚠️ O EMPACOTAMENTO EM LINHAS — intervalos, não dias esparsos
// ---------------------------------------------------------------------------
//
//   `repartirPorLinhas` percorre dia a dia porque um serviço pode ter buracos no
//   meio. Um pedido é sempre um intervalo CONTÍGUO e já conhecido de uma vez —
//   não há buraco a descobrir dia a dia. `empacotarPorPlanear` é o algoritmo
//   clássico de particionamento de intervalos (greedy, ordenado por início,
//   primeira linha livre): é óptimo — o número de linhas que produz é exactamente
//   o máximo de pedidos por planear em simultâneo, nunca mais.
// ============================================================================

import { separarListas, type Pedido } from './pedido.js';
import { inicioEmMs, janelaDoAno } from '@xlv-gva-mpt/dominio';

/** Um pedido por planear, já com o intervalo RECORTADO aos limites do ano. */
export interface ItemPorPlanear {
  readonly pedido: Pedido;
  readonly dia0: number;
  readonly dia1: number;
}

export interface FilaPorPlanearNoMapa {
  /** Uma linha por «pista» — pedidos que nunca se sobrepõem no tempo. Sem tecto. */
  readonly linhas: readonly (readonly ItemPorPlanear[])[];
  /** `dia0`/`dia1` ilegíveis (ver o cabeçalho). Não desenhados. */
  readonly semData: number;
  /** Data legível, fora do ano deste plano. Não desenhados. */
  readonly foraDoAno: number;
  /** `linhas.flat().length + semData + foraDoAno` — o total da fila «por planear». */
  readonly total: number;
}

/**
 * A fila «por planear», pronta a desenhar no mapa: filtrada, recortada ao ano, e
 * já dividida em linhas sem sobreposição.
 *
 * ⚠️ `pedidos` é a resposta CRUA de `/api/pedidos` (fila + reservas do horizonte,
 * como `Pedidos.tsx` já lê) — a filtragem de «por planear» faz-se aqui dentro,
 * pela MESMA função que a lista usa. Passar já filtrado duplicava a regra.
 */
export function filaPorPlanearNoMapa(
  pedidos: readonly Pedido[],
  nDias: number,
): FilaPorPlanearNoMapa {
  const aPlanear = separarListas(pedidos).aPlanear;
  let semData = 0;
  let foraDoAno = 0;
  const itens: ItemPorPlanear[] = [];

  for (const p of aPlanear) {
    // ⚠️ `=== -1` e não `< 0`: -1 é o SENTINELA que `diaDoAno` devolve para uma data
    //    ilegível — um par genuinamente negativo (ex. -8/-3) é uma data VÁLIDA de
    //    antes do ano, e conta-se como `foraDoAno`, não como `semData`. Ver o
    //    cabeçalho do ficheiro para a ambiguidade que fica por resolver na API.
    if (p.dia0 === -1 && p.dia1 === -1) {
      semData++;
      continue;
    }
    const inicio = Math.min(p.dia0, p.dia1);
    const fim = Math.max(p.dia0, p.dia1);
    if (fim < 0 || inicio >= nDias) {
      foraDoAno++;
      continue;
    }
    const dia0 = Math.max(0, inicio);
    const dia1 = Math.min(nDias - 1, fim);
    itens.push({ pedido: p, dia0, dia1 });
  }

  return {
    linhas: empacotarPorPlanear(itens),
    semData,
    foraDoAno,
    total: aPlanear.length,
  };
}

/**
 * Reparte pedidos por «pistas» sem sobreposição, pelo início mais cedo primeiro.
 *
 * ⚠️ **Óptimo, não aproximado:** é o resultado clássico do particionamento de
 * intervalos — ordenar por início e colocar cada intervalo na primeira pista
 * ainda livre produz o número mínimo de pistas possível, que é o máximo de
 * pedidos simultâneos em qualquer dia do ano.
 */
export function empacotarPorPlanear(
  itens: readonly ItemPorPlanear[],
): readonly (readonly ItemPorPlanear[])[] {
  const ordenados = [...itens].sort(
    (a, b) => a.dia0 - b.dia0 || a.dia1 - b.dia1,
  );
  const linhas: ItemPorPlanear[][] = [];
  // `fimDaLinha[r]` = o `dia1` do último item colocado na linha `r`.
  const fimDaLinha: number[] = [];

  for (const item of ordenados) {
    let colocado = false;
    for (let r = 0; r < linhas.length; r++) {
      if ((fimDaLinha[r] ?? -1) < item.dia0) {
        linhas[r]?.push(item);
        fimDaLinha[r] = item.dia1;
        colocado = true;
        break;
      }
    }
    if (!colocado) {
      linhas.push([item]);
      fimDaLinha.push(item.dia1);
    }
  }

  return linhas;
}

/**
 * A ressalva ao lado do título — a mesma disciplina de `.espera-titulo-nota`: diz o que a
 * secção É e o que fazer, e nomeia o que ficou de fora em vez de o esconder (D-081).
 *
 * ⚠️ **Três frases, nunca uma só a somar tudo:** «desenhados» e «sem data» e «fora do ano»
 * são factos diferentes (ver o cabeçalho do ficheiro) — juntá-los numa soma escondia qual
 * deles precisa de acção (uma data por corrigir no pedido) e qual não precisa de nenhuma
 * (uma viagem do ano seguinte).
 */
export function notaDaFilaPorPlanear(fila: FilaPorPlanearNoMapa): string {
  const desenhados = fila.linhas.reduce((n, linha) => n + linha.length, 0);
  const partes: string[] = [
    desenhados > 0
      ? `${String(desenhados)} pedido${desenhados === 1 ? '' : 's'} por planear — clique numa barra para o planear`
      : 'nenhum com data para desenhar nesta janela',
  ];
  if (fila.semData > 0) {
    partes.push(
      `${String(fila.semData)} sem data legível — vejam-se na lista de Pedidos`,
    );
  }
  if (fila.foraDoAno > 0) {
    partes.push(`${String(fila.foraDoAno)} fora deste ano`);
  }
  return partes.join(' · ');
}

/**
 * `sigla → índice em dados.clientes`, para colorir e filtrar um pedido pela MESMA
 * regra da frota (`activo`/`corDoCliente`, que trabalham por índice).
 *
 * ⚠️ **Por SIGLA e não por id:** um `Pedido` (`pedido.ts`) traz `clienteSigla`, e
 * `DadosMapa.clientes` não guarda o id — só `[sigla, nome, cor?]`. A sigla é a
 * identidade visual do sistema de cor (P-30); é a chave que já existe dos dois
 * lados. Uma sigla repetida usa a PRIMEIRA — a mesma convenção que a paleta.
 */
export function indicePorSiglaDeCliente(
  clientes: readonly (readonly [sigla: string, nome: string, cor?: string])[],
): ReadonlyMap<string, number> {
  const m = new Map<string, number>();
  clientes.forEach(([sigla], i) => {
    if (!m.has(sigla)) m.set(sigla, i);
  });
  return m;
}
